import { Building, Check, X } from "lucide-react";
import { useLang } from "@/lib/lang-context";

/**
 * Compact "Estimated condo fees" block — rendered only when the backend
 * supplies a `condo_fee_estimate` (apartments only).
 *
 * Shows monthly + annual range, the tier classification, and the list of
 * assumptions the estimator used (lift / pool / doorman / coastal complex).
 *
 * "Previste / Non previste" toggle
 * --------------------------------
 * The backend doesn't know whether the building is condo-managed; we
 * estimate it from text cues only. Standalone houses (trulli, masserie,
 * villas with no shared services) wrongly get a condo estimate.
 *
 * The toggle lets the user override that:
 *   - `included = true`  → estimate shown, costs counted in cashflow
 *   - `included = false` → estimate visually muted + cashflow re-runs
 *                          with the annual saving added to net rent
 *
 * State lives in `EvaluationSheet` (the parent) so multiple downstream
 * components (MortgageCashflow, RentalAnalysis) can react consistently.
 */
const fmtEur = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(n);

const CondoFees = ({ estimate, included = true, onToggle }) => {
  const { t } = useLang();
  if (!estimate) return null;

  return (
    <section
      data-testid="condo-fees"
      className="px-6 md:px-10 py-6 border-t border-[#E6E4DD]"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Building size={12} strokeWidth={1.5} />
            {t("condo.eyebrow")}
          </div>
          <h3
            className={`font-serif-display text-2xl mt-2 ${
              included ? "text-[#282C2A]" : "text-[#A0A39B] line-through"
            }`}
          >
            {included ? t("condo.title") : t("condo.excludedTitle")}
          </h3>
        </div>
        {included && (
          <span
            data-testid="condo-fees-tier"
            className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-[#F2EFE6] border border-[#E6E4DD] text-[#6C706D] self-start"
          >
            {t(`condo.tier.${estimate.tier}`)}
          </span>
        )}
      </div>

      {/* Numbers block — muted when "Non previste". */}
      {included ? (
        <>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <div className="label-eyebrow">{t("condo.monthly")}</div>
              <div
                data-testid="condo-fees-monthly"
                className="font-mono text-xl mt-2 text-[#282C2A]"
              >
                {fmtEur(estimate.monthly_min_eur)}–{fmtEur(estimate.monthly_max_eur)}
              </div>
            </div>
            <div>
              <div className="label-eyebrow">{t("condo.annual")}</div>
              <div
                data-testid="condo-fees-annual"
                className="font-mono text-xl mt-2 text-[#282C2A]"
              >
                {fmtEur(estimate.annual_min_eur)}–{fmtEur(estimate.annual_max_eur)}
              </div>
            </div>
          </div>

          {estimate.assumptions?.length > 0 && (
            <ul
              data-testid="condo-fees-assumptions"
              className="mt-4 text-xs text-[#6C706D] space-y-1"
            >
              {estimate.assumptions.map((a, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#A0A39B]">·</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <p
          data-testid="condo-fees-excluded-body"
          className="mt-4 text-sm text-[#6C706D] leading-relaxed max-w-2xl"
        >
          {t("condo.excludedBody")}
        </p>
      )}

      {/* Toggle "Previste / Non previste". */}
      <div className="mt-6 pt-5 border-t border-dashed border-[#E6E4DD]">
        <div className="text-xs text-[#6C706D] mb-2">
          {t("condo.toggleLabel")}
        </div>
        <div className="inline-flex border border-[#282C2A]" role="group">
          <button
            type="button"
            data-testid="condo-toggle-yes"
            onClick={() => onToggle?.(true)}
            aria-pressed={included}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-2 transition-colors ${
              included
                ? "bg-[#282C2A] text-white"
                : "bg-white text-[#282C2A] hover:bg-[#F0EFEB]"
            }`}
          >
            <Check size={13} strokeWidth={2} />
            {t("condo.toggleYes")}
          </button>
          <button
            type="button"
            data-testid="condo-toggle-no"
            onClick={() => onToggle?.(false)}
            aria-pressed={!included}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-2 transition-colors border-l border-[#282C2A] ${
              !included
                ? "bg-[#282C2A] text-white"
                : "bg-white text-[#282C2A] hover:bg-[#F0EFEB]"
            }`}
          >
            <X size={13} strokeWidth={2} />
            {t("condo.toggleNo")}
          </button>
        </div>
      </div>
    </section>
  );
};

export default CondoFees;
